import {EventEmitter, Injectable, Injector} from '@angular/core';
import {
  AcVariantsModificationOption,
  OfferModificationCode
} from '../../../consts/offer-modification-code.enum';
import {OfferModificationDestination} from '../../../consts/offer-modification-destination.enum';
import {OfferModification, OfferModificationOption, PriceFrequency} from '../../../models/offer/offer-modification.model';
import {SelectedOfferService} from '../section-services/selected-offer/selected-offer.service';
import {RegistrationService} from '../registration.service';
import {UrlParamsService} from '../url-params.service';
import {Offer} from '../../../models/offer/offer.model';
import {noop} from '../../../utils/utils';
import {LanguageService} from '../../../../shared/services/language.service';
import {ApplicationStateService} from '../../../../shared/services/application-state.service';
import {ProcessRequestStatusMessage} from '../../../models/process-request.model';
import {WebSocketService} from '../websocket/web-socket.service';
import {OfferWebSocketHandlerService} from '../websocket/offer-web-socket-handler.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {MatDialog} from '@angular/material';
import {AssistanceCoverageVariantPricing} from '../../../../shared/sections/web/additional-options-section/assistance-coverages-comparison/assistance-coverages-comparison.config';
import {ScrollHelper} from '../../../../shared/services/scroll-helper';
import {MetricsService} from '../../../../shared/services/metrics.service';
import {MetricsDataModel} from '../../../../shared/components/models/metrics/metrics-data.model';
import {CustomMetricsName} from '../../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../../shared/components/models/metrics/metrics-type.enum';
import {DialogService} from '../../dialog.service';
import {DialogKey} from '../../../../shared/components/dialogs/dialogs-configuration';
import {roundTwoDecimalPlaces} from '../../../../shared/utils/number-utils';
import {OfferModificationsConfigService} from './offer-modifications-config.service';
import {OfferModificationsApiService} from './offer-modifications-api.service';
import {OfferPremiumInstallmentCode} from '../../../consts/offer-premium-installment-code.enum';
import {OfferModificationsCacheService} from './offer-modifications-cache.service';
import {OfferCoverageCode} from '../../../consts/offer-coverages/offer-coverage-code.enum';
import {OfferCoverageDestinyCode} from '../../../enums/offer/offer-coverage-destiny-code.enum';
import {CalculationService} from '../calculation.service';
import {currentYear} from '../../../../shared/utils/time-utils';
import {TextTransformService} from '../../../../shared/services/text-transform.service';
import {TranslateService} from '@ngx-translate/core';
import {OfferService} from '../offer.service';
import {InsuranceCompanyService} from '../../../../shared/services/insurance-company.service';

@Injectable({
  providedIn: 'root'
})
export class OfferModificationsService {

  discountCode: string;
  modifiedOfferEvent: EventEmitter<any> = new EventEmitter<any>();
  modifiedOfferWithErrorEvent: EventEmitter<void> = new EventEmitter<void>();
  nnwValue: number;
  glassValue: number;
  tiresValue: boolean | string;
  batteryValue: boolean | string;
  depreciationPartValue: boolean;
  offerBasicPrice: number;
  offerCurrentPrice: number;
  modifyOfferRequired = false;
  additionalOptionsModified = false;
  acModificationsMode: boolean;
  frequenciesBasicPrices: PremiumFrequencyBasicPrice[] = [];

  private readonly valuationCoverageCode = OfferCoverageCode.AC_VALUE_OF_VEHICLE;

  private webSocketService: WebSocketService;
  private subscriptions = [];
  private scrollHelper: ScrollHelper;

  get offerModificationsEnabled(): boolean {
    return this.api.offerModificationsEnabled;
  }

  get modifications(): OfferModification[] {
    if (this.selectedOfferService.selectedOffer && this.offerModificationsEnabled) {
      return this.selectedOfferService.selectedOffer.modifications;
    }
    return null;
  }

  get recalculatePremiumStepButtonVisible(): boolean {
    return this.configService.isUniqaOrPevno && this.modifications && this.modifications.length && !isNotNullOrUndefined(this.offerCurrentPrice);
  }

  get selectedOffer(): Offer {
    return this.selectedOfferService.selectedOffer;
  }

  get selectedNNWOption(): OfferModificationOption {
    const nnwModification = this.modifications.find(mod => mod.code === OfferModificationCode.NNW);
    if (isNotNullOrUndefined(nnwModification)) {
      return nnwModification.options.find(option => option.selected);
    }
    return null;
  }

  get selectedTiresOption(): OfferModificationOption {
    const tiresModification = this.modifications.find(mod => mod.code === OfferModificationCode.TIRES);
    if (isNotNullOrUndefined(tiresModification)) {
      return tiresModification.options.find(option => option.selected);
    }
    return null;
  }

  get selectedBatteryOption(): OfferModificationOption {
    const batteryModification = this.modifications.find(mod => mod.code === OfferModificationCode.BATTERY);
    if (isNotNullOrUndefined(batteryModification)) {
      return batteryModification.options.find(option => option.selected);
    }
    return null;
  }

  get acExcessNotLessThan1000(): boolean {
    const acExcessModification = this.modifications.find(mod => mod.code === OfferModificationCode.AC_EXCESS);
    if (isNotNullOrUndefined(acExcessModification)) {
      return acExcessModification.options.find(option => option.selected).value >= 1000;
    }
    return false;
  }

  get assistanceStandardOrPremium(): boolean {
    let assistanceValue = this.modifications.find(mod => mod.code === OfferModificationCode.ASSISTANCE).options.find(option => option.selected).value;
    return assistanceValue === 'ASSISTANCE_PREMIUM' || assistanceValue === 'ASSISTANCE_STANDARD';
  }

  get acVariantModification(): OfferModification {
    return this.modifications.find(mod => mod.code === OfferModificationCode.AC_VARIANT);
  }

  get acVariantSerwisASO(): boolean {
    return this.acVariantModification ? this.acVariantModification.options.find(option => option.selected).value === AcVariantsModificationOption.SERWIS_ASO : false;
  }

  get acVariantSerwisASOAvailable(): boolean {
    if (this.acVariantModification) {
      const serwisASOOption = this.acVariantModification.options.find(option => option.value === AcVariantsModificationOption.SERWIS_ASO);
      return isNotNullOrUndefined(serwisASOOption);
    }
    return false;
  }

  get minicascoVariantSelectedOption(): any {
    if (this.hasModification(OfferModificationCode.MINICASCO_VARIANT)) {
      const minicascoModification = this.modifications.find(mod => mod.code === OfferModificationCode.MINICASCO_VARIANT);
      const selectedOption = minicascoModification.options.find(option => option.selected);
      return selectedOption ? selectedOption.value : null;
    }
    return null;
  }

  get callCenterMode(): boolean {
    return this.urlParamsService.isLoggedUserIdParam;
  }

  get selectedOfferPackageName(): string {
    return this.selectedOfferService.selectedOffer.packageName;
  }

  get selectedPremiumExists(): boolean {
    return isNotNullOrUndefined(this.selectedOfferService.selectedOffer.premiums.find(premium => premium.id === this.registration.registration.premiumId));
  }

  get premiumCode(): string {
    if (this.selectedPremiumExists) {
      const selectedPremium = this.selectedOfferService.selectedOffer.premiums.find(premium => premium.id === this.registration.premiumId);
      return selectedPremium.instalments[selectedPremium.instalments.length - 1].code;
    }
    return this.selectedOfferService.selectedOffer.premiums.find(premium => premium.instalments.length === 1).instalments[0].code;
  }

  get vehicleValueModificationEnabled(): boolean {
    return false; // feature disabled until vehicle value modification support
    // return this.isUNIQA && !!this.getSelectedOfferVehicleValue();
  }

  get hasACOrACMini(): boolean {
    return this.selectedOfferService.hasACOrACMini();
  }

  get gsuIncluded(): boolean {
    return currentYear() - this.calculationService.calculationData.vehicle.productionYear <= 3;
  }

  get getOfferEvent(): EventEmitter<Offer> {
    return this.offerService.getOfferEvent;
  }

  get requiresGetOffer(): boolean {
    return this.insuranceCompanyService.isGetOfferRequired(this.stateService.insuranceCompanyCode);
  }

  constructor(public selectedOfferService: SelectedOfferService,
              public urlParamsService: UrlParamsService,
              public languageService: LanguageService,
              public stateService: ApplicationStateService,
              public api: OfferModificationsApiService,
              public injector: Injector,
              public dialog: MatDialog,
              public dialogService: DialogService,
              public metricsService: MetricsService,
              public registration: RegistrationService,
              public configService: OfferModificationsConfigService,
              private cacheService: OfferModificationsCacheService,
              private calculationService: CalculationService,
              private ts: TranslateService,
              private offerService: OfferService,
              private insuranceCompanyService: InsuranceCompanyService) {
  }

  initCache(): void {
    this.discountCode = null;
    this.setBasicPricesForAllFrequencies();
    this.cacheService.init();
    this.cacheService.addEntry(this.createModificationsDigest(), this.frequenciesBasicPrices, this.selectedOffer.coverages);
    this.webSocketService = this.injector.get(WebSocketService);
    this.webSocketService.connect();
    this.initOffersWebSocket();
    this.scrollHelper = new ScrollHelper();
  }

  updateModification(code: string, selectedValue: any): void {
    this.modifyOfferRequired = true;
    if (this.configService.isUniqaOrPevno && code === OfferModificationCode.PARTS_DEPRECIATION) {
      this.depreciationPartValue = selectedValue;
      const estimateValue = selectedValue ? AcVariantsModificationOption.KOSZTORYS_Z_AMORTYZACJA : AcVariantsModificationOption.KOSZTORYS_BEZ_AMORTYZACJI;
      const acVariant = this.modifications.find(mod => mod.code === OfferModificationCode.AC_VARIANT);
      if (acVariant) {
        acVariant.options.forEach(option => option.selected = option.value === estimateValue);
      }
    } else {
      const modification = this.modifications.find(mod => mod.code === code);
      if (modification) {
        modification.options.forEach(option => {
          if (typeof selectedValue === 'boolean' && typeof option.value === 'string') {
            option.selected = option.value === selectedValue + '';
          } else {
            option.selected = option.value === selectedValue;
          }
        });
      }
    }
    this.setRegistrationModificationCoverages();
    this.setRegistrationCurrentAmount();
  }

  saveModificationInCache(): void {
    const digest = this.createModificationsDigest();
    if (this.cacheService.entryExist(digest)) {
      this.frequenciesBasicPrices = this.cacheService.getFrequenciesBasicPrices(digest);
      this.selectedOfferService.coverages = this.cacheService.getCoverages(digest);
      this.offerBasicPrice = this.getFrequencyBasicPricePremiumAmount();
      this.setOfferCurrentPrice();
      this.modifiedOfferEvent.emit();
    } else {
      this.offerBasicPrice = null;
      this.offerCurrentPrice = null;
    }
  }

  getCorrectPriceFrequency(): PriceFrequency {
    const {premiumId} = this.registration.registration;
    const premiums = this.selectedOfferService.selectedOffer.premiums;
    const selectedPremium = premiumId ? premiums.find(premium => premium.id === premiumId) : premiums[0];
    const instalmentCode = selectedPremium.instalments[selectedPremium.instalments.length - 1].code;
    return this.configService.getPriceFrequency(instalmentCode);
  }

  setOfferCurrentPrice(): void {
    if (this.configService.isUniqaOrPevno && this.offerBasicPrice) {
      this.calculateOfferPrice();
    } else if (this.configService.isBeesafe) {
      this.calculateOfferPrice();
    }
  }

  calculateOfferPrice() {
    this.setCurrentPricesForAllFrequencies();
    this.offerCurrentPrice = parseFloat((this.getFrequencyBasicPricePremiumAmount() + this.getModificationsCost()).toFixed(2));
  }

  setRegistrationCurrentAmount() {
    this.registration.registration.currentAmount = this.offerCurrentPrice;
  }

  setRegistrationModificationCoverages() {
    if (this.configService.isBeesafe) {
      const offerParameters = this.selectedOfferService.selectedOffer.modifications
        .map(mod => ({
          code: mod.code,
          value: mod.options.find(option => option.selected).value
        }));

      this.registration.registration.modificationCoverages = offerParameters;
    } else {
      this.registration.registration.modificationCoverages = null;
    }
  }

  setOfferBasicPrice(): void {
    this.setBasicPricesForAllFrequencies();
  }

  setBasicPricesForAllFrequencies(): void {
    this.frequenciesBasicPrices = [];
    this.selectedOfferService.selectedOffer.premiums.forEach(premium => {
      const installmentCode = premium.instalments[premium.instalments.length - 1].code;
      const installmentValue = premium.instalments[premium.instalments.length - 1].value;
      const premiumAmount = premium.amount.amount;
      const modificationsPrice = this.calculateModificationsPrice(this.configService.getPriceFrequency(installmentCode));
      this.frequenciesBasicPrices.push({
        installmentCode: installmentCode,
        frequencyPrice: {
          premiumAmount: premiumAmount - modificationsPrice,
          installmentValue: installmentValue - modificationsPrice
        }
      });
    });
    this.offerBasicPrice = this.getFrequencyBasicPricePremiumAmount();
  }

  calculateModificationsPrice(frequency: PriceFrequency): number {
    if (isNotNullOrUndefined(this.modifications)) {
      return this.modifications
          .filter(modification => isNotNullOrUndefined(modification.options.find(option => option.selected)))
          .map(mod => mod.options.find(option => option.selected))
          .filter(option => option.hasOwnProperty('prices'))
          .map(option => option.prices.find(price => price.frequency === frequency).value)
          .reduce((p1, p2) => p1 + p2, 0);
    }
    return null;
  }

  setCurrentPricesForAllFrequencies(): void {
    this.selectedOfferService.selectedOffer.premiums.forEach(premium => {
      const {code} = premium.instalments[premium.instalments.length - 1];
      const modificationsPrice = this.calculateModificationsPrice(this.configService.getPriceFrequency(code));
      premium.amount.amount = this.getFrequencyBasicPricePremiumAmount(code) + modificationsPrice;
      premium.instalments.forEach(instalment => {
        const rawValue = premium.amount.amount / premium.instalments.length;
        instalment.value = roundTwoDecimalPlaces(rawValue);
      });
    })
  }

  postOfferModification(openWaitingDialog = true): Promise<any> {
    return this.api.postOfferModification({
      paymentFrequency: this.getCorrectPriceFrequency(),
      discountCode: this.discountCode
    }, openWaitingDialog);
  }

  recalculatePremium(): void {
    this.registration.registration.premiumId = null;

    this.postOfferModification()
      .then(resp => {
      })
      .catch(err => {
        this.disconnectWS();
      });
  }

  handleSelectedPremium(premiumId: string): void {
    const selectedPremium = this.selectedOfferService.selectedOffer.premiums.find(premium => premium.id === premiumId);
    if (isNotNullOrUndefined(selectedPremium)) {
      this.stateService.selectedPremiumCode = selectedPremium.instalments[selectedPremium.instalments.length - 1].code;
    }
  }

  getAssistancePricing(): AssistanceCoverageVariantPricing[] {
    const priceFrequency = this.getCorrectPriceFrequency();
    return this.modifications.find(mod => mod.code === OfferModificationCode.ASSISTANCE).options
      .map(option => ({
        name: option.value,
        price: option.prices ? option.prices.find(price => price.frequency === priceFrequency).value : 0
      }));
  }

  getACPricing(): any[] {
    const priceFrequency = this.getCorrectPriceFrequency();
    const acModification = this.configService.isBeesafe ? OfferModificationCode.AC : OfferModificationCode.AC_VARIANT;
    return this.modifications.find(mod => mod.code === acModification).options
      .map(option => ({
        name: option.value,
        price: option.prices ? option.prices.find(price => price.frequency === priceFrequency).value : 0
      }));
  }

  getNNWPricing(): AssistanceCoverageVariantPricing[] {
    const priceFrequency = this.getCorrectPriceFrequency();
    return this.modifications.find(mod => mod.code === OfferModificationCode.NNW).options
      .map(option => ({
        name: option.value,
        price: option.prices ? option.prices.find(price => price.frequency === priceFrequency).value : 0
      }));
  }

  createModificationsDigest(): string {
    const values = {};
    this.configService.getModificationsForDestination(OfferModificationDestination.AC_VARIANTS)
      .forEach(config => {
        const modification = this.modifications.find(mod => mod.code === config.code);
        if (modification) {
          const selectedOption = modification.options.find(option => option.selected);
          values[config.code] = selectedOption ? selectedOption.value : null;
        } else {
          if (config.code === OfferModificationCode.AC_GSU) { // mock false value if AC_GSU isn't present
            values[config.code] = this.acVariantSerwisASO;
          }
        }
      });
    this.configService.isUniqa ? values['UNIQA_DISCOUNT_CODE'] = this.discountCode ? this.discountCode : '' : noop();
    return JSON.stringify(values);
  }

  getModificationsCost(): number {
    const priceFrequency = this.getCorrectPriceFrequency();
    return this.modifications
      .filter(modification => isNotNullOrUndefined(modification.options.find(option => option.selected)))
      .map(mod => mod.options.find(option => option.selected))
      .filter(option => option.hasOwnProperty('prices'))
      .map(option => option.prices.find(price => price.frequency === priceFrequency).value)
      .reduce((p1, p2) => p1 + p2, 0);
  }

  getSelectedOfferVehicleValue() {
    const offerCoverage = this.selectedOffer.coverages.filter(coverage => coverage.code === this.valuationCoverageCode)[0];
    const coverageDestiny = offerCoverage.destinies.filter(destiny => destiny.code === OfferCoverageDestinyCode.DETAIL_BOX)[0];
    const selectedOption = coverageDestiny.options.filter(option => option.code === coverageDestiny.selectedOption)[0];

    let vehicleValue;
    if (selectedOption.type === 'STRING' || selectedOption.type === 'NUMBER') {
      vehicleValue = Number.parseInt(this.languageService.getTranslationForKey(selectedOption.values, 'value'));
    } else if (selectedOption.type === 'BOOLEAN') {
      const value = this.languageService.getTranslationForKey(selectedOption.values, 'value');
      if (!value) {
        vehicleValue = null;
      }
    } else {
      vehicleValue = null;
    }
    return vehicleValue;
  }

  getVehicleValueFormatted(): string {
    const vehicleValue = this.getSelectedOfferVehicleValue() + '';
    const currency = this.ts.instant('OFFER_SECTION.SHARED.currencyShortcut');
    const vehicleValueFormatted = TextTransformService.separateValues(vehicleValue, {
      charsChain: 3,
      separator: ' '
    });
    return `${vehicleValueFormatted} ${currency}`;
  }

  hasModification(code: OfferModificationCode | string): boolean {
    return !!this.modifications.find(modification => modification.code === code);
  }

  getSelectedOfferOriginalModification(code: OfferModificationCode): OfferModification | null {
    if (!!this.stateService.selectedOfferOriginalModifications) {
      return this.stateService.selectedOfferOriginalModifications.find(modification => modification.code === code);
    }
    return null;
  }

  getSelectedOfferOriginalModificationSelectedOptionValue(code: OfferModificationCode): any {
    const modification = this.getSelectedOfferOriginalModification(code);
    if (modification) {
      const selectedOption = modification.options.find(option => option.selected);
      return selectedOption ? selectedOption.value : null;
    }
    return null;
  }

  isModificationWithSelect(code: string): boolean {
    const config = this.configService.getModificationConfig(code as OfferModificationCode);
    return config ? config.withSelect : false;
  }

  getSelectedOption(code: OfferModificationCode): OfferModificationOption {
    const modification = this.modifications.find(mod => mod.code === code);
    if (isNotNullOrUndefined(modification)) {
      return modification.options.find(option => option.selected);
    }
    return null;
  }

  getOptionWithMinimumNonZeroPrice(modification: OfferModification): OfferModificationOption {
    const minNonZeroPrice = Math.min(...modification.options
        .filter(option => isNotNullOrUndefined(option.prices))
        .map(option => option.prices
            .find(price => price.frequency === this.getCorrectPriceFrequency()).value
        )
        .filter(priceValue => priceValue > 0));
    return modification.options
        .find(option => !!option.prices.find(price => price.frequency === this.getCorrectPriceFrequency() && price.value === minNonZeroPrice));
  }

  unsubscribeFromEvents(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  private initOffersWebSocket(): void {
    let offerWebSocketHandlerService: OfferWebSocketHandlerService = this.injector.get(OfferWebSocketHandlerService);

    let offerSubscription = offerWebSocketHandlerService.offerObservable
      .subscribe(processRequest => {
        if (this.selectedOfferPackageName === processRequest.body.offer.packageName) {
          this.dialog.closeAll();
          if (processRequest.status.message !== ProcessRequestStatusMessage.ERROR) {
            this.additionalOptionsModified = false;
            this.modifyOfferRequired = false;
            this.stateService.selectedOffer = processRequest.body.offer;
            this.sendModifiedOfferMetric(processRequest.body.offer);
            this.stateService.addOfferHeader(processRequest.header);
            this.stateService.updateApplicationState();
            this.setPremium();
            this.registration.offerId = processRequest.body.offer.id;
            const prevPrice = this.offerCurrentPrice;
            this.setOfferBasicPrice();
            this.setOfferCurrentPrice();
            this.setRegistrationModificationCoverages();
            this.setRegistrationCurrentAmount();
            this.cacheService.addEntry(this.createModificationsDigest(), this.frequenciesBasicPrices, this.selectedOffer.coverages);
            const priceChanged = prevPrice !== this.offerCurrentPrice;
            if (priceChanged && !this.acModificationsMode) {
              this.sendChangedPriceOfferMetric(prevPrice);
            }
            this.modifiedOfferEvent.emit({
              priceChanged: priceChanged
            });
          } else {
            this.modifiedOfferWithErrorEvent.emit();
            this.handleError(<string>processRequest.status.codeDescription)
          }
        }
      });

    this.subscriptions.push(offerSubscription);
  }

  private sendModifiedOfferMetric(offer: Offer): void {
    this.metricsService.sendSimpleMetric(CustomMetricsName.MODIFIED_OFFER, MetricsType.RESPONSE, offer.id);
  }

  private handleError(codeDescription: string): void {
    this.sendErrorOfferMetric(codeDescription);
    if (codeDescription.includes('Nieprawidłowy kod promocyjny')) {
      this.dialogService.open(DialogKey.INCORRECT_DISCOUNT_CODE);
    }
  }

  private sendErrorOfferMetric(error: string): void {
    const metric = new MetricsDataModel(CustomMetricsName.MODIFIED_ERROR_OFFER, MetricsType.PROCESS);
    metric.change(JSON.stringify({
      offerId: this.selectedOfferService.selectedOffer.id,
      error: error,
      modificationData: this.api.lastSentModificationData
    }));
    this.metricsService.send(metric);
  }

  private sendChangedPriceOfferMetric(previousPrice: number): void {
    const metric = new MetricsDataModel(CustomMetricsName.MODIFIED_CHANGED_PRICE_OFFER, MetricsType.PROCESS);
    metric.change(JSON.stringify({
      previousPrice: previousPrice,
      newPrice: this.offerCurrentPrice,
      modificationData: this.api.lastSentModificationData
    }));
    this.metricsService.send(metric);
  }

  private setPremium(): void {
    if (!this.selectedPremiumExists) {
      const newPremium = this.selectedOfferService.selectedOffer.premiums.find(premium => {
        return premium.instalments[premium.instalments.length - 1].code === this.stateService.selectedPremiumCode;
      });
      this.registration.premiumId = newPremium ? newPremium.id : null;
    }
  }

  private disconnectWS() {
    this.webSocketService.disconnect();
    this.unsubscribeFromEvents();
  }

  private getFrequencyBasicPricePremiumAmount(installmentCode: string = this.premiumCode): number {
    const frequencyBasicPrice = this.getFrequencyBasicPrice(installmentCode);
    return frequencyBasicPrice ? frequencyBasicPrice.premiumAmount : null;
  }

  private getFrequencyBasicPriceInstallmentValue(installmentCode: string): number {
    const frequencyBasicPrice = this.getFrequencyBasicPrice(installmentCode);
    return frequencyBasicPrice ? frequencyBasicPrice.installmentValue : null;
  }

  private getFrequencyBasicPrice(installmentCode: string): PremiumFrequencyPrice {
    const priceItem = this.frequenciesBasicPrices.find(item => item.installmentCode === installmentCode);
    return priceItem ? priceItem.frequencyPrice : null;
  }
}

export interface PremiumFrequencyBasicPrice {
  installmentCode: OfferPremiumInstallmentCode | string;
  frequencyPrice: PremiumFrequencyPrice;
}

export interface PremiumFrequencyPrice {
  premiumAmount: number,
  installmentValue: number
}
